import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, Inbox } from 'lucide-react';
import { ContactMessage } from '../../types';
import apiService from '../../services/api';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import useEscapeToClose from '../../hooks/useEscapeToClose';

// ─── Badge helpers ────────────────────────────────────────────────────────────

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  nuevo:       { label: 'Nuevo',       className: 'bg-red-100 text-red-800 border border-red-200' },
  leido:       { label: 'Leído',       className: 'bg-gray-100 text-gray-700 border border-gray-200' },
  respondido:  { label: 'Respondido',  className: 'bg-green-100 text-green-700 border border-green-200' },
  archivado:   { label: 'Archivado',   className: 'bg-blue-100 text-blue-700 border border-blue-200' },
};

const SUBJECT_LABEL: Record<string, string> = {
  cotizacion:          'Cotización',
  informacion_general: 'Info general',
};

const LOCAL_TIMEZONE = 'America/Argentina/Buenos_Aires';
const DEFAULT_QUOTE_RESPONSE =
  'Buen dia!\n' +
  'Te envio la cotizacion actualizada para que la puedas revisar.\n\n' +
  'Cualquier duda o si necesitas ajustar cantidades o medidas, avisame y lo vemos.\n' +
  'Tambien, si tenes alguna medida especial o uso especifico, te puedo recomendar la bolsa mas adecuada.\n\n' +
  'Quedo atento. Gracias!';

function buildQuoteTemplate(message: ContactMessage): string {
  const resumen = (message.message || '').replace(/\s+/g, ' ').trim().slice(0, 140);
  const resumenLinea = resumen ? `Detalle recibido: ${resumen}\n\n` : '';
  return (
    'Buen dia!\n' +
    'Te envio la cotizacion actualizada para que la puedas revisar.\n\n' +
    resumenLinea +
    'Cualquier duda o si necesitas ajustar cantidades o medidas, avisame y lo vemos.\n' +
    'Tambien, si tenes alguna medida especial o uso especifico, te puedo recomendar la bolsa mas adecuada.\n\n' +
    'Quedo atento. Gracias!'
  );
}

function parseBackendDate(value?: string) {
  if (!value) return null;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const asUtc = normalized.endsWith('Z') ? normalized : `${normalized}Z`;
  const date = new Date(asUtc);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTime(value?: string) {
  const date = parseBackendDate(value);
  if (!date) return '-';
  return date.toLocaleString('es-AR', { timeZone: LOCAL_TIMEZONE });
}

function formatDateTimeCompact(value?: string) {
  const date = parseBackendDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('es-AR', {
    timeZone: LOCAL_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusBadge({ status }: { status: string }) {
  const config = STATUS_BADGE[status] ?? { label: status, className: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${config.className}`}>
      {config.label}
    </span>
  );
}

// ─── Delete confirmation inline ───────────────────────────────────────────────

function DeleteConfirm({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) {
  return (
    <span className="inline-flex items-center gap-2 text-xs">
      <span className="text-gray-600">¿Confirmar?</span>
      <button onClick={onConfirm} className="text-red-600 font-medium hover:underline">Sí</button>
      <button onClick={onCancel}  className="text-gray-500 hover:underline">No</button>
    </span>
  );
}

// ─── Skeleton loader ──────────────────────────────────────────────────────────

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="border-t animate-pulse">
          {Array.from({ length: 6 }).map((__, j) => (
            <td key={j} className="px-4 py-3">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const Messages: React.FC = () => {
  const [messages,         setMessages]         = useState<ContactMessage[]>([]);
  const [isLoading,        setIsLoading]        = useState(true);
  const [loadError,        setLoadError]        = useState<string | null>(null);
  const [isRetrying,       setIsRetrying]       = useState(false);
  const [searchTerm,       setSearchTerm]       = useState('');
  const [selectedStatus,   setSelectedStatus]   = useState('all');
  const [selectedSubject,  setSelectedSubject]  = useState('all');
  const [selectedMessage,  setSelectedMessage]  = useState<ContactMessage | null>(null);
  const [confirmDeleteId,  setConfirmDeleteId]  = useState<number | null>(null);
  useBodyScrollLock(!!selectedMessage);
  useEscapeToClose(!!selectedMessage, () => setSelectedMessage(null));
  const [actionError,      setActionError]      = useState<string | null>(null);
  const [responseDraft,    setResponseDraft]    = useState('');
  const [isSendingResponse, setIsSendingResponse] = useState(false);
  const [responseAttachment, setResponseAttachment] = useState<File | null>(null);

  // ── Fetch ────────────────────────────────────────────────────────────────────

  const fetchMessages = async (isManualRetry = false) => {
    if (isManualRetry) setIsRetrying(true);
    try {
      setLoadError(null);
      const primary = await apiService.getContactMessages({ page: 1, limit: 200 });
      setMessages(primary.messages || []);
    } catch {
      try {
        const fallback = await apiService.getContactMessages();
        setMessages(fallback.messages || []);
      } catch (fallbackError: any) {
        const apiMessage = fallbackError?.response?.data?.message;
        setLoadError(
          apiMessage
            ? `No se pudieron cargar los mensajes. ${String(apiMessage)}`
            : 'No se pudieron cargar los mensajes.'
        );
      }
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  };

  useEffect(() => { fetchMessages(); }, []);

  // ── Derived state ─────────────────────────────────────────────────────────────

  const filteredMessages = useMemo(() => {
    return messages.filter((m) => {
      if (selectedStatus  !== 'all' && m.status  !== selectedStatus)  return false;
      if (selectedSubject !== 'all' && m.subject !== selectedSubject) return false;
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return (
        m.name.toLowerCase().includes(q)    ||
        m.email.toLowerCase().includes(q)   ||
        (m.company || '').toLowerCase().includes(q) ||
        m.message.toLowerCase().includes(q)
      );
    });
  }, [messages, selectedStatus, selectedSubject, searchTerm]);

  const pendingCount = useMemo(
    () => messages.filter((m) => m.status === 'nuevo').length,
    [messages]
  );

  // ── Actions ───────────────────────────────────────────────────────────────────

  const markRead = async (id: number) => {
    try {
      setActionError(null);
      await apiService.markContactMessageRead(id);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, status: 'leido' } : m)));
      setSelectedMessage((prev) => (prev?.id === id ? { ...prev, status: 'leido' } : prev));
      window.dispatchEvent(new CustomEvent('admin:messages-updated'));
    } catch (error) {
      console.error('Error marking message read:', error);
    }
  };

  const deleteMessage = async (id: number) => {
    try {
      setActionError(null);
      await apiService.deleteContactMessage(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setSelectedMessage((prev) => (prev?.id === id ? null : prev));
      setConfirmDeleteId(null);
      window.dispatchEvent(new CustomEvent('admin:messages-updated'));
    } catch (error: any) {
      console.error('Error deleting message:', error);
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setActionError(detail || 'No se pudo eliminar el mensaje.');
      setConfirmDeleteId(null);
    }
  };

  // Al abrir el detalle: auto-marcar como leído si está "nuevo"
  const openMessage = (m: ContactMessage) => {
    setSelectedMessage(m);
    if (m.response) {
      setResponseDraft(m.response);
    } else if (m.subject === 'cotizacion') {
      setResponseDraft(buildQuoteTemplate(m));
    } else {
      setResponseDraft('');
    }
    setResponseAttachment(null);
    if (m.status === 'nuevo') markRead(m.id);
  };

  const respondMessage = async () => {
    if (!selectedMessage) return;
    const responseText = responseDraft.trim() || DEFAULT_QUOTE_RESPONSE;

    try {
      setActionError(null);
      setIsSendingResponse(true);
      await apiService.respondContactMessage(selectedMessage.id, responseText, responseAttachment);
      const respondedAt = new Date().toISOString();

      setMessages((prev) =>
        prev.map((m) =>
          m.id === selectedMessage.id
            ? { ...m, status: 'respondido', response: responseText, respondedAt }
            : m
        )
      );
      setSelectedMessage((prev) =>
        prev?.id === selectedMessage.id
          ? { ...prev, status: 'respondido', response: responseText, respondedAt }
          : prev
      );
      window.dispatchEvent(new CustomEvent('admin:messages-updated'));
      setSelectedMessage(null);
      setResponseDraft('');
      setResponseAttachment(null);
    } catch (error: any) {
      const detail = error?.response?.data?.detail || error?.response?.data?.message;
      setActionError(detail || 'No se pudo enviar la respuesta.');
    } finally {
      setIsSendingResponse(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Encabezado */}
      <div>
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-3 sm:text-2xl">
          Gestión de Mensajes
          {pendingCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-red-600 text-white text-xs font-semibold">
              {pendingCount > 99 ? '99+' : pendingCount}
            </span>
          )}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {messages.length > 0
            ? `${messages.length} mensaje${messages.length !== 1 ? 's' : ''} en total · ${pendingCount} sin leer`
            : 'Administra los mensajes de contacto'}
        </p>
      </div>

      {/* Error banner */}
      {loadError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 flex items-center justify-between gap-4">
          <span className="text-sm">{loadError}</span>
          <button
            onClick={() => fetchMessages(true)}
            disabled={isRetrying}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded border border-red-300 hover:bg-red-100 disabled:opacity-60 transition-colors"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRetrying ? 'animate-spin' : ''}`} />
            {isRetrying ? 'Reintentando...' : 'Reintentar'}
          </button>
        </div>
      )}
      {actionError && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          {actionError}
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="relative col-span-2 md:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nombre, email o mensaje..."
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="all">Todos los estados</option>
          <option value="nuevo">Nuevo</option>
          <option value="leido">Leído</option>
          <option value="respondido">Respondido</option>
          <option value="archivado">Archivado</option>
        </select>
        <select
          value={selectedSubject}
          onChange={(e) => setSelectedSubject(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="all">Todos los asuntos</option>
          <option value="cotizacion">Cotización</option>
          <option value="informacion_general">Info general</option>
        </select>
      </div>

      {/* Tarjetas (celular) */}
      <div className="divide-y rounded-lg border border-gray-100 bg-white shadow-sm md:hidden">
        {isLoading ? (
          <p className="py-10 text-center text-sm text-gray-500">Cargando mensajes...</p>
        ) : filteredMessages.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
            <Inbox className="h-8 w-8 opacity-40" />
            <span className="text-sm">{searchTerm || selectedStatus !== 'all' || selectedSubject !== 'all' ? 'No hay mensajes que coincidan con los filtros.' : 'No hay mensajes todavía.'}</span>
          </div>
        ) : filteredMessages.map((m) => (
          <div key={m.id} className={`space-y-2 p-4 ${m.status === 'nuevo' ? 'border-l-2 border-l-red-400' : ''}`}>
            <button onClick={() => openMessage(m)} className="block w-full space-y-1 text-left">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-gray-900">{m.name}</span>
                <span className="shrink-0 text-xs text-gray-500">{formatDateTimeCompact(m.createdAt)}</span>
              </div>
              <p className="truncate text-sm text-gray-600">{m.email}</p>
              <div className="flex items-center gap-2">
                <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200">{SUBJECT_LABEL[m.subject] ?? m.subject}</span>
                <StatusBadge status={m.status} />
              </div>
            </button>
            {confirmDeleteId === m.id ? (
              <DeleteConfirm onConfirm={() => deleteMessage(m.id)} onCancel={() => setConfirmDeleteId(null)} />
            ) : (
              <div className="flex gap-2">
                <button onClick={() => openMessage(m)} className="flex-1 rounded border border-blue-200 px-2 py-1.5 text-xs text-blue-700">Ver</button>
                {m.status === 'nuevo' && <button onClick={() => markRead(m.id)} className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-xs text-primary-700">Marcar leído</button>}
                <button onClick={() => setConfirmDeleteId(m.id)} className="flex-1 rounded border border-red-200 px-2 py-1.5 text-xs text-red-700">Eliminar</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Tabla */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-100 overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Nombre</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Email</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Asunto</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Estado</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Fecha</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Acción</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows />
            ) : filteredMessages.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <Inbox className="h-8 w-8 opacity-40" />
                    <span className="text-sm">
                      {searchTerm || selectedStatus !== 'all' || selectedSubject !== 'all'
                        ? 'No hay mensajes que coincidan con los filtros.'
                        : 'No hay mensajes todavía.'}
                    </span>
                  </div>
                </td>
              </tr>
            ) : (
              filteredMessages.map((m) => (
                <tr
                  key={m.id}
                  onClick={() => openMessage(m)}
                  className={`border-t cursor-pointer hover:bg-gray-50 transition-colors ${
                    m.status === 'nuevo' ? 'border-l-2 border-l-red-400' : ''
                  }`}
                >
                  <td className="px-4 py-3 font-medium text-gray-900">{m.name}</td>
                  <td className="px-4 py-3 text-gray-600">{m.email}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600 border border-gray-200">
                      {SUBJECT_LABEL[m.subject] ?? m.subject}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={m.status} />
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                    {formatDateTimeCompact(m.createdAt)}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {confirmDeleteId === m.id ? (
                      <DeleteConfirm
                        onConfirm={() => deleteMessage(m.id)}
                        onCancel={() => setConfirmDeleteId(null)}
                      />
                    ) : (
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => openMessage(m)}
                          className="text-blue-600 hover:underline text-xs"
                        >
                          Ver
                        </button>
                        {m.status === 'nuevo' && (
                          <button
                            onClick={() => markRead(m.id)}
                            className="text-primary-600 hover:underline text-xs"
                          >
                            Marcar leído
                          </button>
                        )}
                        <button
                          onClick={() => setConfirmDeleteId(m.id)}
                          className="text-red-500 hover:underline text-xs"
                        >
                          Eliminar
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal detalle */}
      {selectedMessage && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] sm:max-h-[90vh] flex flex-col">

            {/* Header modal */}
            <div className="px-6 py-4 border-b flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-semibold text-gray-900">Detalle del mensaje</h2>
                <StatusBadge status={selectedMessage.status} />
              </div>
              <button
                onClick={() => { setSelectedMessage(null); setResponseAttachment(null); }}
                className="text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Body modal */}
            <div className="p-6 space-y-3 text-sm overflow-y-auto flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-0.5">Nombre</span>
                  <span className="text-gray-900 font-medium">{selectedMessage.name}</span>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-0.5">Email</span>
                  <a href={`mailto:${selectedMessage.email}`} className="text-blue-600 hover:underline">
                    {selectedMessage.email}
                  </a>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-0.5">Teléfono</span>
                  <span className="text-gray-900">{selectedMessage.phone || '-'}</span>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-0.5">Empresa</span>
                  <span className="text-gray-900">{selectedMessage.company || '-'}</span>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-0.5">Asunto</span>
                  <span className="text-gray-900">{SUBJECT_LABEL[selectedMessage.subject] ?? selectedMessage.subject}</span>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-0.5">Fecha</span>
                  <span className="text-gray-900">{formatDateTime(selectedMessage.createdAt)}</span>
                </div>
              </div>

              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Mensaje</span>
                <div className="whitespace-pre-wrap bg-gray-50 border border-gray-100 rounded-lg p-4 text-gray-800 leading-relaxed">
                  {selectedMessage.message}
                </div>
              </div>

              {selectedMessage.response && (
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Respuesta enviada</span>
                  <div className="whitespace-pre-wrap bg-green-50 border border-green-100 rounded-lg p-4 text-gray-800 leading-relaxed">
                    {selectedMessage.response}
                  </div>
                </div>
              )}

              <div>
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">Responder</span>
                <textarea
                  value={responseDraft}
                  onChange={(e) => setResponseDraft(e.target.value)}
                  placeholder="Escribe la respuesta para este mensaje..."
                  rows={5}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                <div className="mt-2 flex flex-col gap-2">
                  <input
                    type="file"
                    onChange={(e) => setResponseAttachment(e.target.files?.[0] || null)}
                    className="w-full text-sm text-gray-700"
                  />
                  {responseAttachment && (
                    <span className="text-xs text-gray-500">
                      Adjunto: {responseAttachment.name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Footer modal */}
            <div className="px-6 py-4 border-t flex justify-between items-center gap-3">
              <div>
                {confirmDeleteId === selectedMessage.id ? (
                  <DeleteConfirm
                    onConfirm={() => deleteMessage(selectedMessage.id)}
                    onCancel={() => setConfirmDeleteId(null)}
                  />
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(selectedMessage.id)}
                    className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    Eliminar
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={respondMessage}
                  disabled={isSendingResponse}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
                >
                  {isSendingResponse ? 'Enviando...' : 'Enviar respuesta'}
                </button>
                {selectedMessage.subject === 'cotizacion' && (
                  <button
                    onClick={() => {
                      const params = new URLSearchParams({
                        messageId: String(selectedMessage.id),
                        name: selectedMessage.name || '',
                        email: selectedMessage.email || '',
                        company: selectedMessage.company || '',
                      });
                      window.open(`/admin/cotizador?${params.toString()}`, '_blank', 'noopener,noreferrer');
                    }}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Abrir cotizador
                  </button>
                )}
                <button
                  onClick={() => { setSelectedMessage(null); setResponseAttachment(null); }}
                  className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cerrar
                </button>
                {selectedMessage.status !== 'leido' && selectedMessage.status !== 'respondido' && (
                  <button
                    onClick={() => markRead(selectedMessage.id)}
                    className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Marcar leído
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
